import { z } from 'zod';
import { attachmentContentTypeSchema } from './attachment';
import { claimStatusEnumValues } from './baseClaim';

export const conversationKinds = ['direct', 'group'] as const;
export const conversationKindSchema = z.enum(conversationKinds);
export type ConversationKind = z.infer<typeof conversationKindSchema>;

export const conversationParticipantSchema = z
  .object({
    id: z.guid(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    status: z.string().nullable(),
  })
  .strict();

export const messageAttachmentSchema = z
  .object({
    id: z.guid(),
    originalFilename: z.string().min(1),
  })
  .strict();

export const chatClaimSummarySchema = z
  .object({
    id: z.guid(),
    claimNumber: z.string().nullable(),
    serviceDate: z.iso.date(),
    status: z.enum(claimStatusEnumValues).nullable(),
  })
  .strict();

export const chatClaimReferenceSchema = z.discriminatedUnion('available', [
  z
    .object({
      available: z.literal(true),
      claim: chatClaimSummarySchema,
    })
    .strict(),
  z.object({ available: z.literal(false) }).strict(),
]);

export const conversationMessageSchema = z
  .object({
    id: z.guid(),
    clientMessageID: z.guid().nullable(),
    conversationID: z.guid(),
    senderID: z.guid(),
    content: z.string(),
    createdAt: z.string(),
    attachments: z.array(messageAttachmentSchema),
    claimReference: chatClaimReferenceSchema.nullable().default(null),
  })
  .strict();

export const conversationSummarySchema = z
  .object({
    id: z.guid(),
    organizationID: z.guid(),
    kind: conversationKindSchema,
    title: z.string().nullable(),
    creatorID: z.guid(),
    createdAt: z.string(),
    updatedAt: z.string(),
    participants: z.array(conversationParticipantSchema),
    latestMessage: conversationMessageSchema.nullable(),
    unreadCount: z.number().int().nonnegative(),
    isActiveMember: z.boolean(),
  })
  .strict();

export const conversationListRequestSchema = z
  .object({
    cursor: z.string().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(50).default(25),
  })
  .strict();

export const conversationListResponseSchema = z
  .object({
    data: z.array(conversationSummarySchema),
    nextCursor: z.string().nullable(),
  })
  .strict();

export const conversationMessagesRequestSchema = z
  .object({
    conversationID: z.guid(),
    cursor: z.string().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();

export const conversationMessagesResponseSchema = z
  .object({
    data: z.array(conversationMessageSchema),
    nextCursor: z.string().nullable(),
    isActiveMember: z.boolean(),
  })
  .strict();

export const conversationClaimSearchRequestSchema = z
  .object({
    conversationID: z.guid(),
    query: z.string().trim().max(100).optional(),
    claimID: z.guid().optional(),
    cursor: z.string().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
  })
  .strict()
  .refine((value) => !(value.query && value.claimID), {
    message: 'Search by query or claim ID, not both.',
  });

export const conversationClaimSearchResponseSchema = z
  .object({
    data: z.array(chatClaimSummarySchema),
    nextCursor: z.string().nullable(),
  })
  .strict();

export const createConversationSchema = z
  .object({
    kind: conversationKindSchema,
    title: z.string().trim().min(1).max(120).optional(),
    participantIDs: z.array(z.guid()).min(1).max(49),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.kind === 'direct' && value.participantIDs.length !== 1) {
      context.addIssue({
        code: 'custom',
        path: ['participantIDs'],
        message: 'Direct conversations require exactly one other participant.',
      });
    }
    if (value.kind === 'group' && value.participantIDs.length < 2) {
      context.addIssue({
        code: 'custom',
        path: ['participantIDs'],
        message: 'Group conversations require at least two other participants.',
      });
    }
    if (value.kind === 'group' && !value.title) {
      context.addIssue({
        code: 'custom',
        path: ['title'],
        message: 'Group conversations require a title.',
      });
    }
  });

export const updateConversationSchema = z
  .object({
    conversationID: z.guid(),
    title: z.string().trim().min(1).max(120),
    participantIDs: z.array(z.guid()).min(2).max(49),
  })
  .strict();

export const conversationMutationResponseSchema = z
  .object({ conversationID: z.guid() })
  .strict();

export const markConversationReadSchema = z
  .object({ conversationID: z.guid(), messageID: z.guid() })
  .strict();

export const markConversationReadResponseSchema = markConversationReadSchema;

export const prepareMessageAttachmentsSchema = z
  .object({
    conversationID: z.guid(),
    files: z
      .array(
        z
          .object({
            originalFilename: z.string().trim().min(1).max(255),
            contentType: attachmentContentTypeSchema,
            sizeBytes: z
              .number()
              .int()
              .min(1)
              .max(10 * 1024 * 1024),
          })
          .strict()
      )
      .min(1)
      .max(5),
  })
  .strict();

export const messageAttachmentUploadTargetSchema = messageAttachmentSchema
  .extend({
    sizeBytes: z
      .number()
      .int()
      .min(1)
      .max(10 * 1024 * 1024),
    uploadURL: z.url(),
  })
  .strict();
export const prepareMessageAttachmentsResponseSchema = z.array(
  messageAttachmentUploadTargetSchema
);

export const messageAttachmentDownloadRequestSchema = z
  .object({ attachmentID: z.guid() })
  .strict();
export const messageAttachmentDownloadResponseSchema = z
  .object({ downloadURL: z.url() })
  .strict();

export const chatMessageSendSchema = z
  .object({
    action: z.literal('chat.message.send'),
    clientMessageID: z.guid(),
    conversationID: z.guid(),
    content: z.string().trim().max(4000).default(''),
    attachmentIDs: z.array(z.guid()).max(5).default([]),
    claimID: z.guid().nullable().default(null),
  })
  .strict()
  .refine(
    (value) =>
      value.content.length > 0 ||
      value.attachmentIDs.length > 0 ||
      value.claimID !== null,
    {
      message: 'A message or attachment is required.',
    }
  );

export const chatMessageCreatedEnvelopeSchema = z
  .object({
    action: z.literal('chat.message.created'),
    message: conversationMessageSchema,
  })
  .strict();

export const chatConversationUpdatedEnvelopeSchema = z
  .object({
    action: z.literal('chat.conversation.updated'),
    conversationID: z.guid(),
  })
  .strict();

export const chatMessageRejectedEnvelopeSchema = z
  .object({
    action: z.literal('chat.message.rejected'),
    clientMessageID: z.guid(),
    reason: z.string().min(1),
  })
  .strict();

export const chatWebSocketEnvelopeSchema = z.union([
  chatMessageCreatedEnvelopeSchema,
  chatConversationUpdatedEnvelopeSchema,
  chatMessageRejectedEnvelopeSchema,
]);

export type ConversationMessage = z.infer<typeof conversationMessageSchema>;
export type ConversationSummary = z.infer<typeof conversationSummarySchema>;
export type ConversationListResponse = z.infer<
  typeof conversationListResponseSchema
>;
export type ConversationMessagesResponse = z.infer<
  typeof conversationMessagesResponseSchema
>;
export type MessageAttachment = z.infer<typeof messageAttachmentSchema>;
export type ChatClaimSummary = z.infer<typeof chatClaimSummarySchema>;
export type ChatClaimReference = z.infer<typeof chatClaimReferenceSchema>;
export type ConversationClaimSearchResponse = z.infer<
  typeof conversationClaimSearchResponseSchema
>;
export type MessageAttachmentUploadTarget = z.infer<
  typeof messageAttachmentUploadTargetSchema
>;
export type ChatMessageSend = z.infer<typeof chatMessageSendSchema>;
export type ChatWebSocketEnvelope = z.infer<typeof chatWebSocketEnvelopeSchema>;
